import { ChevronDownIcon } from "lucide-react";
import { useState } from "react";
import type { CmmdWorkItemType } from "@cmmd-forge/contracts";

import { Button } from "~/components/ui/button";
import { useUiStateStore } from "~/uiStateStore";
import {
  ProjectTaskCreateFooter,
  ProjectTaskCreateProjectLeadAction,
} from "./ProjectTaskCreateFooter";
import { ProjectTaskCreateProperties } from "./ProjectTaskCreateProperties";
import { buildCreateTaskParams } from "./projectTaskCreateParams";
import { ProjectTaskPicker } from "./ProjectTaskPicker";
import {
  createAssigneeOptions,
  createMilestoneOptions,
  createPriorityOptions,
  createStatusOptions,
  initialCreateStatusSelection,
  optionalTaskId,
  taskIdValue,
  type ProjectTaskCreatePriority,
} from "./projectTaskCreateOptions";
import {
  isTaskCreationDisabled,
  resolveCreateStatusSelection,
  resolvedCreateStatusSelection,
  selectedCreateStatusValue,
} from "./projectTaskCreateWorkflow";
import { useProjectLeadTaskDraft } from "./projectLeadCreation/useProjectLeadTaskDraft";
import { ProjectTaskCreateDialogRender, TaskCreateError } from "./ProjectTaskCreateDialogRender";
import {
  ActiveProjectLeadWorkbench,
  AgentMemberRosterError,
  agentMembersOrEmpty,
  createProjectLeadCloseHandler,
  defaultTodoWorkflowStageId,
  handleDialogClose,
  isCreateFormDisabled,
  isProjectLeadStatusSupported,
  markdownChangeHandler,
  ProjectLeadRestoreRetry,
  resolveProjectLeadUnavailableReason,
  statusChangeHandler,
  visibleMoreProperties,
} from "./ProjectTaskCreateDialogProjectLead";
import {
  initialMilestoneId,
  projectLeadDraftIdentity,
  submitTaskDraft,
  type ProjectTaskCreateDialogProps,
} from "./ProjectTaskCreateDialogContract";

export function ProjectTaskCreateDialog(props: ProjectTaskCreateDialogProps) {
  return props.open ? <ProjectTaskCreateDialogForm {...props} /> : null;
}

const priorityOrDefault = (priority: ProjectTaskCreatePriority | null) => priority ?? "medium";

function ProjectTaskCreateDialogForm(props: ProjectTaskCreateDialogProps) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [statusSelectionOverride, setStatusSelectionOverride] = useState<ReturnType<
    typeof initialCreateStatusSelection
  > | null>(null);
  const statusSelection = resolvedCreateStatusSelection(
    statusSelectionOverride,
    props.workflow,
    props.target,
  );
  const creationDisabled = isTaskCreationDisabled(props);
  const [priority, setPriority] = useState<ProjectTaskCreatePriority | null>(null);
  const [assignment, setAssignment] = useState<string | null>(null);
  const [milestoneId, setMilestoneId] = useState<number | null>(initialMilestoneId(props.target));
  const [parentTaskId, setParentTaskId] = useState<number | null>(null);
  const [workItemType, setWorkItemType] = useState<CmmdWorkItemType>("other");
  const [dueDate, setDueDate] = useState("");
  const [dependencyIds, setDependencyIds] = useState<readonly number[]>([]);
  const [createMore, setCreateMore] = useState(false);
  const [showMoreProperties, setShowMoreProperties] = useState(false);
  const projectLeadDraft = useProjectLeadTaskDraft({
    ...projectLeadDraftIdentity(props),
    onThreadReady: props.onProjectLeadThreadReady,
    onCommitted: props.onTaskTreeCommitted,
    onDiscarded: () => useUiStateStore.getState().closeProjectLeadCompanion(),
  });
  const projectLeadUnavailableReason = resolveProjectLeadUnavailableReason({
    hasProjectIdentity: props.projectId != null && props.projectRef != null,
    creationDisabled,
    statusIsSupported: isProjectLeadStatusSupported({
      status: statusSelection.status,
      workflowStageId: statusSelection.workflowStageId,
      defaultWorkflowStageId: defaultTodoWorkflowStageId(props.workflow),
    }),
    hasAssignee: assignment !== null,
    hasMilestone: milestoneId !== null,
    hasParentTask: parentTaskId !== null,
    hasDueDate: dueDate.trim().length > 0,
    hasDependencies: dependencyIds.length > 0,
  });
  const formDisabled = isCreateFormDisabled(
    creationDisabled,
    projectLeadDraft,
    projectLeadUnavailableReason === undefined,
  );
  const requestClose = createProjectLeadCloseHandler({
    draft: projectLeadDraft,
    pendingRetryAvailable: projectLeadUnavailableReason === undefined,
    onClose: () => props.onOpenChange(false),
  });
  const handleOpenChange = (open: boolean) => handleDialogClose(open, requestClose);

  const workbench = (
    <ActiveProjectLeadWorkbench
      draft={projectLeadDraft}
      projectTitle={props.projectTitle}
      onClose={requestClose}
    />
  );
  if (projectLeadDraft.draft) return workbench;

  const submit = async () => {
    const trimmedTitle = title.trim();
    await submitTaskDraft({
      disabled: creationDisabled,
      params: buildCreateTaskParams({
        title: trimmedTitle,
        description,
        status: statusSelection.status,
        priority,
        workItemType,
        assignee: null,
        dueDate,
        parentTaskId,
        milestoneId,
        workflowStageId: statusSelection.workflowStageId,
        dependencyIds,
      }),
      assignment,
      createMore,
      onCreate: props.onCreate,
      onReset: () => {
        setTitle("");
        setDescription("");
        setDependencyIds([]);
      },
      onClose: () => props.onOpenChange(false),
    });
  };

  const planningFields = (
    <div className="grid grid-cols-2 gap-2 md:flex md:flex-wrap" aria-label="Task planning fields">
      <PickerWidth className="w-full min-w-0 md:w-40">
        <ProjectTaskPicker
          ariaLabel="Status"
          value={selectedCreateStatusValue(statusSelection.status, statusSelection.workflowStageId)}
          emptyLabel="Status"
          options={createStatusOptions(props.workflow)}
          disabled={formDisabled}
          presentation="chip"
          onValueChange={statusChangeHandler(
            props.workflow,
            setStatusSelectionOverride,
            resolveCreateStatusSelection,
          )}
        />
      </PickerWidth>
      <PickerWidth className="w-full min-w-0 md:w-36">
        <ProjectTaskPicker
          ariaLabel="Priority"
          value={priority}
          emptyLabel="Priority"
          options={createPriorityOptions()}
          disabled={formDisabled}
          presentation="chip"
          emptyVisual={{ kind: "placeholder", icon: "priority" }}
          onValueChange={(value) => setPriority(value as ProjectTaskCreatePriority | null)}
        />
      </PickerWidth>
      <PickerWidth className="w-full min-w-0 md:w-44">
        <ProjectTaskPicker
          ariaLabel="Assignee"
          value={assignment}
          emptyLabel={props.agentMembersLoading ? "Loading agents…" : "Assignee"}
          options={createAssigneeOptions(props.members, agentMembersOrEmpty(props.agentMembers))}
          disabled={formDisabled || props.agentMembersLoading === true}
          presentation="chip"
          emptyVisual={{ kind: "placeholder", icon: "assignee" }}
          onValueChange={setAssignment}
        />
        <AgentMemberRosterError error={props.agentMembersError} />
      </PickerWidth>
      <PickerWidth className="w-full min-w-0 md:w-52">
        <ProjectTaskPicker
          ariaLabel="Milestone"
          value={taskIdValue(milestoneId)}
          emptyLabel="Milestone"
          options={createMilestoneOptions(props.milestones)}
          disabled={formDisabled}
          presentation="chip"
          emptyVisual={{ kind: "placeholder", icon: "milestone" }}
          onValueChange={(value) => setMilestoneId(optionalTaskId(value))}
        />
      </PickerWidth>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        aria-expanded={showMoreProperties}
        aria-controls="task-create-more-properties"
        disabled={formDisabled}
        className="col-span-2 min-h-touch justify-start rounded-md px-2 text-muted-foreground md:col-span-1 md:min-h-8 md:rounded-full"
        onClick={() => setShowMoreProperties((current) => !current)}
      >
        More properties
        <ChevronDownIcon
          className={
            showMoreProperties ? "rotate-180 transition-transform" : "transition-transform"
          }
          aria-hidden
        />
      </Button>
    </div>
  );
  const moreProperties = visibleMoreProperties(
    showMoreProperties,
    <ProjectTaskCreateProperties
      tasks={props.tasks}
      parentTaskId={parentTaskId}
      workItemType={workItemType}
      dueDate={dueDate}
      dependencyIds={dependencyIds}
      disabled={formDisabled}
      onParentTaskIdChange={setParentTaskId}
      onWorkItemTypeChange={setWorkItemType}
      onDueDateChange={setDueDate}
      onDependencyIdsChange={setDependencyIds}
    />,
  );
  const footer = (
    <ProjectTaskCreateFooter
      createMore={createMore}
      creating={props.creating}
      projectLeadStartBusy={projectLeadDraft.busy}
      projectLeadPending={projectLeadDraft.pendingCreate}
      projectLeadAvailable={projectLeadUnavailableReason === undefined}
      canCreateTask={title.trim().length > 0 && !creationDisabled}
      onCreateMoreChange={setCreateMore}
    />
  );
  const headerAction = (
    <ProjectTaskCreateProjectLeadAction
      busy={props.creating || projectLeadDraft.busy || projectLeadDraft.restoring}
      pending={projectLeadDraft.pendingCreate}
      available={projectLeadUnavailableReason === undefined}
      unavailableReason={projectLeadUnavailableReason}
      onStart={() =>
        void projectLeadDraft.start({
          title,
          description,
          priority: priorityOrDefault(priority),
          workItemType,
        })
      }
    />
  );
  const errors = (
    <>
      <TaskCreateError error={props.error} />
      <TaskCreateError error={projectLeadDraft.error} />
      <ProjectLeadRestoreRetry
        restoring={projectLeadDraft.restoring}
        error={projectLeadDraft.error}
        onRetry={projectLeadDraft.retryRestore}
      />
    </>
  );
  return (
    <ProjectTaskCreateDialogRender
      projectTitle={props.projectTitle}
      disabled={formDisabled}
      title={title}
      description={description}
      headerAction={headerAction}
      planningFields={planningFields}
      moreProperties={moreProperties}
      footer={footer}
      errors={errors}
      onOpenChange={handleOpenChange}
      onSubmit={() => void submit()}
      onTitleChange={setTitle}
      onDescriptionChange={markdownChangeHandler(formDisabled, setDescription)}
    />
  );
}

function PickerWidth(props: { readonly className: string; readonly children: React.ReactNode }) {
  return <div className={props.className}>{props.children}</div>;
}
